import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../../shared/assets/logos/ARBCOR_Text_only.png';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setIsMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const sections = [
    { label: 'Platform', id: 'platform' },
    { label: 'Capabilities', id: 'features' },
    { label: 'Industries', id: 'industries' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <>
      <nav className={`fixed z-50 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isScrolled
          ? 'top-4 left-1/2 -translate-x-1/2 w-auto'
          : 'top-0 left-0 right-0'
      }`}>
        <div className={`transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isScrolled
            ? 'bg-zinc-900/95 backdrop-blur-md rounded-full px-2 py-1.5 shadow-lg shadow-zinc-900/10'
            : 'bg-white/90 backdrop-blur-sm'
        }`}>
          <div className={`flex items-center justify-between transition-all duration-700 ${
            isScrolled
              ? 'gap-1 h-10'
              : 'max-w-7xl mx-auto px-6 lg:px-8 h-14'
          }`}>
            {/* Logo */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`flex-shrink-0 transition-all duration-700 ${
                isScrolled ? 'px-4' : ''
              }`}
            >
              <img
                src={logo}
                alt="Arbcor"
                className={`w-auto select-none pointer-events-none transition-all duration-700 ${
                  isScrolled ? 'h-4 invert' : 'h-6'
                }`}
                style={isScrolled ? { filter: 'invert(1)' } : undefined}
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
            </button>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-1">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`text-[12px] tracking-wide transition-all duration-500 ${
                    isScrolled
                      ? 'text-zinc-400 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/10'
                      : 'text-zinc-400 hover:text-zinc-900 px-3 py-1'
                  }`}
                >
                  {s.label}
                </button>
              ))}
              <span className={`w-px h-3 mx-1 transition-colors duration-700 ${
                isScrolled ? 'bg-zinc-700' : 'bg-zinc-200'
              }`} />
              <Link
                to="/login"
                className={`text-[12px] tracking-wide font-medium transition-all duration-500 ${
                  isScrolled
                    ? 'text-zinc-300 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/10'
                    : 'text-zinc-600 hover:text-zinc-900 px-3 py-1'
                }`}
              >
                Log in
              </Link>
              <Link
                to="/signup"
                className={`text-[12px] tracking-wide font-medium transition-all duration-500 ${
                  isScrolled
                    ? 'text-zinc-900 px-4 py-1.5 rounded-full bg-white hover:bg-zinc-200'
                    : 'text-white bg-zinc-900 hover:bg-zinc-800 px-4 py-1.5 rounded-full'
                }`}
              >
                Sign up
              </Link>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`md:hidden w-8 h-8 flex flex-col items-end justify-center gap-1.5 transition-all duration-700 ${
                isScrolled ? 'pr-3' : ''
              }`}
              aria-label="Menu"
            >
              <span className={`block h-px transition-all duration-300 ${
                isScrolled ? 'bg-white' : 'bg-zinc-900'
              } ${isMobileOpen ? 'w-6 rotate-45 translate-y-[3.5px]' : 'w-6'}`} />
              <span className={`block h-px transition-all duration-300 ${
                isScrolled ? 'bg-white' : 'bg-zinc-900'
              } ${isMobileOpen ? 'w-6 -rotate-45 -translate-y-[3.5px]' : 'w-4'}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`fixed inset-x-0 z-40 bg-white border-b border-zinc-100 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isMobileOpen
          ? `opacity-100 translate-y-0 ${isScrolled ? 'top-[72px]' : 'top-[56px]'}`
          : `opacity-0 -translate-y-4 pointer-events-none ${isScrolled ? 'top-[72px]' : 'top-[56px]'}`
      }`}>
        <div className="px-6 py-8 flex flex-col gap-5">
          {sections.map((s) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className="text-left text-lg font-light text-zinc-900 hover:text-zinc-500 transition-colors"
            >
              {s.label}
            </button>
          ))}
          <div className="h-px bg-zinc-100 my-2" />
          <Link
            to="/login"
            onClick={() => setIsMobileOpen(false)}
            className="text-lg font-light text-zinc-900 hover:text-zinc-500 transition-colors"
          >
            Log in
          </Link>
          <Link
            to="/signup"
            onClick={() => setIsMobileOpen(false)}
            className="inline-flex items-center justify-center px-6 py-3 bg-zinc-900 text-white text-sm tracking-wider uppercase hover:bg-zinc-800 transition-colors duration-300"
          >
            Sign up
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;
